import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    RefreshCw, Loader2, Activity, TrendingUp, TrendingDown, Minus,
    Clock, ArrowRight, Target,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import { AD_LIVE_DASHBOARD_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";

const HEALTH = {
    green:   { pill: "bg-emerald-500", ring: "border-emerald-200 dark:border-emerald-900/50", label: "On target" },
    amber:   { pill: "bg-amber-500",   ring: "border-amber-200 dark:border-amber-900/50",     label: "Drifting" },
    red:     { pill: "bg-red-500",     ring: "border-red-200 dark:border-red-900/50",         label: "Over target" },
    unknown: { pill: "bg-gray-300 dark:bg-gray-600", ring: "border-border",                   label: "No target set" },
};

const num = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN"));
const money = (v) =>
    v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", {
        minimumFractionDigits: 0, maximumFractionDigits: 0,
    })}`;

const Delta = ({ value }) => {
    if (value == null) return <span className="text-muted-foreground text-[11px]">no data yesterday</span>;
    const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
    const tone = value > 0 ? "text-amber-600 dark:text-amber-400"
        : value < 0 ? "text-emerald-600 dark:text-emerald-400"
            : "text-muted-foreground";
    return (
        <span className={cn("inline-flex items-center gap-0.5 text-[11px] font-medium", tone)}>
            <Icon className="h-3 w-3" />
            {Math.abs(value).toFixed(0)}% vs yesterday
        </span>
    );
};

const HeaderStat = ({ label, value, sub, accent }) => (
    <div className="rounded-xl border border-border bg-card p-4">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
        <p className={cn("text-2xl font-bold mt-1", accent || "text-foreground")}>{value}</p>
        {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
);

export default function AdLiveDashboard() {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cplTarget, setCplTarget] = usePersistedState("ad_dash_cpl_target", "");

    const load = useCallback(() => {
        setLoading(true);
        const qs = cplTarget ? `?cpl_target=${encodeURIComponent(cplTarget)}` : "";
        fetch(`${AD_LIVE_DASHBOARD_URL}${qs}`)
            .then(res => {
                if (res?.status === "success") setData(res);
                else ToastMsg("error", res?.message || "Failed to load dashboard");
            })
            .catch(() => ToastMsg("error", "Failed to load dashboard"))
            .finally(() => setLoading(false));
    }, [cplTarget]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const today = data?.today || {};
    const mtd = data?.mtd || {};
    const campaigns = data?.campaigns || [];

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Activity className="h-6 w-6 text-primary" />
                            Live Ad Spend
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5" />
                            {/* Meta's own numbers lag, and this reads a nightly sync on top of that —
                                saying so is cheaper than fielding "why doesn't this match Ads Manager". */}
                            {data?.last_synced_at
                                ? `Data as of the last sync: ${data.last_synced_at}`
                                : "No sync has run yet"}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-1.5">
                            <Target className="h-4 w-4 text-muted-foreground" />
                            <Input
                                type="number"
                                placeholder="CPL target ₹"
                                value={cplTarget}
                                onChange={e => setCplTarget(e.target.value)}
                                className="h-9 w-32 text-sm"
                            />
                        </div>
                        <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                            Refresh
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => navigate("/reports/ad-performance")}>
                            Full report
                            <ArrowRight className="h-4 w-4 ml-1.5" />
                        </Button>
                    </div>
                </div>

                {/* Today / MTD roll-up */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <HeaderStat label="Spend today" value={money(today.spend)}
                        sub={`${money(mtd.spend)} month to date`} />
                    <HeaderStat label="Leads today" value={num(today.meta_leads)}
                        sub={`${num(mtd.meta_leads)} MTD`} />
                    <HeaderStat label="CPL today" value={money(today.cpl)}
                        accent={
                            today.cpl == null || !data?.cpl_target ? undefined
                                : today.cpl <= data.cpl_target
                                    ? "text-emerald-600 dark:text-emerald-400"
                                    : "text-red-600 dark:text-red-400"
                        }
                        sub={`${money(mtd.cpl)} MTD`} />
                    <HeaderStat label="CRM leads today" value={num(today.crm_leads)}
                        sub={`${num(mtd.crm_leads)} MTD`} />
                    <HeaderStat label={sessionStorage.getItem("product_type") === "engage" ? "Appointments MTD" : "Demos MTD"} value={num(mtd.demos)}
                        sub={`${num(mtd.contacted)} contacted`} />
                    <HeaderStat label="Won MTD" value={num(mtd.won)}
                        sub={mtd.cac != null ? `${money(mtd.cac)} CAC` : "no wins yet"} />
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {!loading && campaigns.length === 0 && (
                    <div className="rounded-xl border border-border bg-card p-10 text-center">
                        <p className="text-sm text-muted-foreground">
                            No campaign has recorded spend this month. Connect an ad account and run a
                            sync from Settings → Meta Ads.
                        </p>
                    </div>
                )}

                {/* Campaign cards */}
                {!loading && campaigns.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {campaigns.map(c => {
                            const health = HEALTH[c.health] || HEALTH.unknown;
                            return (
                                <div key={c.campaign_id}
                                    className={cn("rounded-xl border bg-card p-4 space-y-3", health.ring)}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={cn("h-2 w-2 rounded-full shrink-0", health.pill)}
                                                    title={health.label} />
                                                <p className="text-sm font-semibold text-foreground truncate"
                                                    title={c.name}>{c.name}</p>
                                            </div>
                                            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                                                {c.effective_status} · {c.objective}
                                                {c.project_name && ` · ${c.project_name}`}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <p className="text-[10px] uppercase text-muted-foreground">Today</p>
                                            <p className="text-base font-bold text-foreground">{money(c.today.spend)}</p>
                                            <Delta value={c.spend_vs_yesterday} />
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase text-muted-foreground">Leads</p>
                                            <p className="text-base font-bold text-foreground">{num(c.today.meta_leads)}</p>
                                            <p className="text-[11px] text-muted-foreground">{num(c.mtd.meta_leads)} MTD</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase text-muted-foreground">
                                                CPL {c.health_basis === "mtd" ? "(MTD)" : "(today)"}
                                            </p>
                                            <p className={cn("text-base font-bold",
                                                c.health === "green" ? "text-emerald-600 dark:text-emerald-400"
                                                    : c.health === "red" ? "text-red-600 dark:text-red-400"
                                                        : c.health === "amber" ? "text-amber-600 dark:text-amber-400"
                                                            : "text-foreground")}
                                                title={c.health_basis === "mtd"
                                                    ? "Too few leads today to judge — graded on month to date"
                                                    : "Graded on today's leads"}>
                                                {money(c.health_cpl)}
                                            </p>
                                            <p className="text-[11px] text-muted-foreground">
                                                {c.health_basis === "mtd"
                                                    ? `${money(c.today.cpl)} today`
                                                    : `${money(c.mtd.cpl)} MTD`}
                                            </p>
                                        </div>
                                    </div>

                                    {c.daily_budget != null && (
                                        <div>
                                            <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                                                <span>Daily budget {money(c.daily_budget)}</span>
                                                <span>{c.budget_used_pct != null ? `${c.budget_used_pct.toFixed(0)}%` : "—"}</span>
                                            </div>
                                            <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                                <div className={cn("h-full rounded-full",
                                                    (c.budget_used_pct || 0) >= 95 ? "bg-red-500"
                                                        : (c.budget_used_pct || 0) >= 70 ? "bg-amber-500"
                                                            : "bg-emerald-500")}
                                                    style={{ width: `${Math.min(c.budget_used_pct || 0, 100)}%` }} />
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between text-[11px] pt-1 border-t border-border">
                                        <span className="text-muted-foreground">
                                            MTD: {num(c.mtd.crm_leads)} CRM · {num(c.mtd.demos)} demos · {num(c.mtd.won)} won
                                        </span>
                                        <span className="text-muted-foreground">{money(c.mtd.spend)}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </ContainerCard>
    );
}
